import { ImageOffIcon, PlayIcon } from "lucide-react";
import { memo } from "react";
import type { ArtifactVariant } from "@/api/artifacts";
import { Skeleton } from "@/components/ui/skeleton";
import { useArtifactUrl } from "@/lib/artifact-cache";
import { cn } from "@/lib/utils";
import { useRecord } from "@/sync/hooks";

/** Image artifact from the local blob cache (thumbnail by default). Videos show their poster. */
export const ArtifactImage = memo(function ArtifactImage({
	artifactId,
	variant = "thumb",
	className,
	alt = "",
	fit = "cover",
}: {
	artifactId: string;
	variant?: ArtifactVariant;
	className?: string;
	alt?: string;
	fit?: "cover" | "contain";
}) {
	const record = useRecord("artifact", artifactId);
	const video = record?.kind === "video";
	// Imported media is copied into storage in the background; there is no file until then.
	const waiting = record?.status === "pending" && !record.r2Key;
	const { url, error } = useArtifactUrl(
		waiting
			? null
			: { id: artifactId, kind: record?.kind ?? "image", thumbnailR2Key: record?.thumbnailR2Key },
		video ? "thumb" : variant,
	);
	if (error || (record?.status === "failed" && !record.r2Key)) {
		return (
			<div
				className={cn("flex items-center justify-center bg-muted text-muted-foreground", className)}
			>
				<ImageOffIcon className="size-4" />
			</div>
		);
	}
	if (!url) return <Skeleton className={cn("rounded-none", className)} />;
	const img = (
		<img
			src={url}
			alt={alt || record?.title || ""}
			draggable={false}
			decoding="async"
			className={cn(
				fit === "cover" ? "object-cover" : "object-contain",
				video ? "size-full" : className,
			)}
		/>
	);
	if (!video) return img;
	return (
		<span className={cn("relative block overflow-hidden", className)}>
			{img}
			<span className="absolute bottom-1 left-1 flex size-5 items-center justify-center rounded-full bg-black/55 text-white">
				<PlayIcon className="size-2.5 translate-x-px fill-current" />
			</span>
		</span>
	);
});
